"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "@/spa/routerCompat";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis, Area, AreaChart } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { fetchWithCsrf } from "./fetchWithCsrf";
import { ProjectRuntimeLogsPanel, type ProjectRuntimeLogsBootstrap } from "./ProjectRuntimeLogsPanel";
import { resolveApiUrl } from "./resolveApiUrl";
import { formatBytes } from "../../utils/format";

export type ProjectObservabilityBootstrap = {
  projectId: string;
  projectName: string;
  projectRole: "owner" | "editor" | "viewer";
  runtimeLogs: ProjectRuntimeLogsBootstrap;
};

type MetricsPayload = {
  rangeDays: number;
  bucket: string;
  successRate: number | null;
  terminalInRange: { success: number; failed: number };
  buildDurationSeconds: { p50: number | null; p95: number | null };
  backlog: { queued: number; building: number; oldestQueuedAt: string | null };
  buckets: { t: string; success: number; failed: number; started: number }[];
};

type TrafficPayload = {
  rangeDays: number;
  deploymentFilter: string | null;
  byDay: { t: string; count: number }[];
  latencyByDay: { t: string; p50Ms: number | null; p95Ms: number | null }[];
  errorRateByDay: { t: string; total: number; errorRate: number | null }[];
  byStatus: { statusCode: number; count: number }[];
  topIps: { clientIp: string; count: number }[];
  byPathBucket: { pathBucket: string; count: number }[];
  byDeployment: { deploymentId: string; shortId: string; count: number }[];
};

type UptimePayload = {
  rangeDays: number;
  bucket: string;
  uptimePercent: number | null;
  probes: { total: number; success: number; failed: number };
  latencyMs: { p50: number | null; p95: number | null };
  latestProbe: {
    ok: boolean;
    latencyMs: number | null;
    probedAt: string;
    statusCode: number | null;
  } | null;
  runtimeHealth: {
    status: "ok" | "recovering" | "image_missing" | "ensure_failed" | null;
    detail: string | null;
    checkedAt: string | null;
  };
  buckets: {
    t: string;
    up: number;
    down: number;
    availabilityPercent: number | null;
    latencyP50Ms: number | null;
    latencyP95Ms: number | null;
  }[];
};

type ResourcesPayload = {
  rangeDays: number;
  bucket: string;
  latest: {
    cpuPercent: number | null;
    memoryBytes: number;
    memoryLimitBytes: number | null;
    sampledAt: string;
  } | null;
  buckets: {
    t: string;
    avgCpuPercent: number | null;
    maxCpuPercent: number | null;
    avgMemoryBytes: number | null;
    maxMemoryBytes: number | null;
  }[];
};

type DestinationRow = {
  id: string;
  projectId: string;
  webhookUrl: string;
  createdAt: string;
};

type RuleRow = {
  id: string;
  projectId: string;
  destinationId: string;
  destinationWebhookUrl: string;
  ruleType: "consecutive_failures" | "queue_stall";
  threshold: number;
  cooldownSeconds: number;
  enabled: boolean;
  lastFiredAt: string | null;
  createdAt: string;
  updatedAt: string;
};

const formatDuration = (sec: number | null, emDash: string): string => {
  if (sec === null || !Number.isFinite(sec)) return emDash;
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${(sec / 60).toFixed(1)}m`;
  return `${(sec / 3600).toFixed(1)}h`;
};

const formatAxisDate = (iso: string, bucket: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (bucket === "hour") {
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const ProjectObservabilityPageClient = ({ bootstrap }: { bootstrap: ProjectObservabilityBootstrap }) => {
  const { t } = useTranslation();
  const { projectId, projectName, projectRole, runtimeLogs } = bootstrap;
  const [searchParams, setSearchParams] = useSearchParams();
  const trafficDeploymentFilter = searchParams.get("deploymentId");

  const deployChartConfig = useMemo(
    () =>
      ({
        success: { label: t("projectObservability.chartLegendSuccess"), color: "hsl(142, 76%, 36%)" },
        failed: { label: t("projectObservability.chartLegendFailed"), color: "hsl(0, 84%, 60%)" }
      }) satisfies ChartConfig,
    [t]
  );

  const trafficChartConfig = useMemo(
    () =>
      ({
        count: { label: t("projectObservability.chartLegendRequests"), color: "hsl(221, 83%, 53%)" }
      }) satisfies ChartConfig,
    [t]
  );

  const uptimeChartConfig = useMemo(
    () =>
      ({
        availabilityPercent: {
          label: t("projectObservability.runtimeAvailability"),
          color: "hsl(142, 76%, 36%)"
        },
        latencyP50Ms: { label: t("projectObservability.latencyP50"), color: "hsl(221, 83%, 53%)" },
        latencyP95Ms: { label: t("projectObservability.latencyP95"), color: "hsl(262, 83%, 58%)" }
      }) satisfies ChartConfig,
    [t]
  );

  const resourceChartConfig = useMemo(
    () =>
      ({
        avgCpuPercent: { label: t("projectObservability.cpuAvg"), color: "hsl(38, 92%, 50%)" },
        avgMemoryMb: { label: t("projectObservability.memoryAvg"), color: "hsl(199, 89%, 48%)" }
      }) satisfies ChartConfig,
    [t]
  );

  const latencyTrafficChartConfig = useMemo(
    () =>
      ({
        p50Ms: { label: t("projectObservability.latencyP50"), color: "hsl(221, 83%, 53%)" },
        p95Ms: { label: t("projectObservability.latencyP95"), color: "hsl(262, 83%, 58%)" }
      }) satisfies ChartConfig,
    [t]
  );

  const errorRateChartConfig = useMemo(
    () =>
      ({
        errorRate: { label: t("projectObservability.errorRate"), color: "hsl(0, 84%, 60%)" }
      }) satisfies ChartConfig,
    [t]
  );
  const [rangeDays, setRangeDays] = useState<7 | 30>(7);
  const [metricsBucket, setMetricsBucket] = useState<"hour" | "day">("day");
  const [metrics, setMetrics] = useState<MetricsPayload | null>(null);
  const [traffic, setTraffic] = useState<TrafficPayload | null>(null);
  const [uptime, setUptime] = useState<UptimePayload | null>(null);
  const [resources, setResources] = useState<ResourcesPayload | null>(null);
  const [destinations, setDestinations] = useState<DestinationRow[]>([]);
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [ruleDestinationId, setRuleDestinationId] = useState("");
  const [ruleType, setRuleType] = useState<"consecutive_failures" | "queue_stall">(
    "consecutive_failures"
  );
  const [ruleThreshold, setRuleThreshold] = useState("3");
  const [testWebhookUrl, setTestWebhookUrl] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);

  const loadMetrics = useCallback(async () => {
    const res = await fetch(
      resolveApiUrl(`/api/projects/${projectId}/observability/metrics?rangeDays=${rangeDays}&bucket=${metricsBucket}`),
      { credentials: "include" }
    );
    if (!res.ok) throw new Error(t("projectObservability.loadMetricsFailed"));
    setMetrics((await res.json()) as MetricsPayload);
  }, [projectId, rangeDays, metricsBucket, t]);

  const loadTraffic = useCallback(async () => {
    const qs = new URLSearchParams();
    qs.set("rangeDays", String(rangeDays));
    const depFilter = trafficDeploymentFilter?.trim();
    if (depFilter) qs.set("deploymentId", depFilter);
    const res = await fetch(resolveApiUrl(`/api/projects/${projectId}/observability/traffic?${qs.toString()}`), {
      credentials: "include"
    });
    if (!res.ok) throw new Error(t("projectObservability.loadTrafficFailed"));
    setTraffic((await res.json()) as TrafficPayload);
  }, [projectId, rangeDays, trafficDeploymentFilter, t]);

  const loadUptime = useCallback(async () => {
    const res = await fetch(
      resolveApiUrl(
        `/api/projects/${projectId}/observability/uptime?rangeDays=${rangeDays}&bucket=${metricsBucket}`
      ),
      { credentials: "include" }
    );
    if (!res.ok) throw new Error(t("projectObservability.loadUptimeFailed"));
    setUptime((await res.json()) as UptimePayload);
  }, [projectId, rangeDays, metricsBucket, t]);

  const loadResources = useCallback(async () => {
    const res = await fetch(
      resolveApiUrl(
        `/api/projects/${projectId}/observability/resources?rangeDays=${rangeDays}&bucket=${metricsBucket}`
      ),
      { credentials: "include" }
    );
    if (!res.ok) throw new Error(t("projectObservability.loadResourcesFailed"));
    setResources((await res.json()) as ResourcesPayload);
  }, [projectId, rangeDays, metricsBucket, t]);

  const loadAlerts = useCallback(async () => {
    const [dRes, rRes] = await Promise.all([
      fetch(resolveApiUrl(`/api/projects/${projectId}/observability/alerts/destinations`), { credentials: "include" }),
      fetch(resolveApiUrl(`/api/projects/${projectId}/observability/alerts/rules`), { credentials: "include" })
    ]);
    if (!dRes.ok || !rRes.ok) throw new Error(t("projectObservability.loadAlertsFailed"));
    setDestinations((await dRes.json()) as DestinationRow[]);
    setRules((await rRes.json()) as RuleRow[]);
  }, [projectId, t]);

  const refreshAll = useCallback(async () => {
    setLoadError(null);
    try {
      await Promise.all([loadMetrics(), loadTraffic(), loadUptime(), loadResources(), loadAlerts()]);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t("common.fetchFailed"));
    }
  }, [loadAlerts, loadMetrics, loadResources, loadTraffic, loadUptime, t]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  const handleAddDestination = async () => {
    setLoadError(null);
    const res = await fetchWithCsrf(`/api/projects/${projectId}/observability/alerts/destinations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ webhookUrl: newWebhookUrl.trim() })
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      setLoadError(j.error ?? t("projectObservability.addDestinationFailed"));
      return;
    }
    setNewWebhookUrl("");
    await loadAlerts();
  };

  const handleDeleteDestination = async (id: string) => {
    const res = await fetchWithCsrf(
      `/api/projects/${projectId}/observability/alerts/destinations/${id}`,
      { method: "DELETE" }
    );
    if (!res.ok) {
      setLoadError(t("projectObservability.deleteDestinationFailed"));
      return;
    }
    await loadAlerts();
  };

  const handleAddRule = async () => {
    if (!ruleDestinationId || ruleDestinationId === "__none__") {
      setLoadError(t("projectObservability.pickWebhook"));
      return;
    }
    const threshold = Number.parseInt(ruleThreshold, 10);
    if (!Number.isFinite(threshold)) {
      setLoadError(t("projectObservability.invalidThreshold"));
      return;
    }
    const res = await fetchWithCsrf(`/api/projects/${projectId}/observability/alerts/rules`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        destinationId: ruleDestinationId,
        ruleType,
        threshold
      })
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      setLoadError(j.error ?? t("projectObservability.createRuleFailed"));
      return;
    }
    setLoadError(null);
    await loadAlerts();
  };

  const handleDeleteRule = async (id: string) => {
    const res = await fetchWithCsrf(`/api/projects/${projectId}/observability/alerts/rules/${id}`, {
      method: "DELETE"
    });
    if (!res.ok) {
      setLoadError(t("projectObservability.deleteRuleFailed"));
      return;
    }
    await loadAlerts();
  };

  const handleToggleRule = async (rule: RuleRow) => {
    const res = await fetchWithCsrf(`/api/projects/${projectId}/observability/alerts/rules/${rule.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled: !rule.enabled })
    });
    if (!res.ok) {
      setLoadError(t("projectObservability.updateRuleFailed"));
      return;
    }
    await loadAlerts();
  };

  const handleTestWebhook = async () => {
    setTestResult(null);
    const res = await fetchWithCsrf(`/api/projects/${projectId}/observability/alerts/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ webhookUrl: testWebhookUrl.trim() })
    });
    const j = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      status?: number;
      error?: string;
      bodyPreview?: string;
    };
    if (res.ok && j.ok) {
      setTestResult(t("projectObservability.testWebhookOk", { status: String(j.status ?? "?") }));
    } else {
      setTestResult(j.error ?? `HTTP ${j.status ?? res.status} ${j.bodyPreview ?? ""}`);
    }
  };

  const bucketChartData =
    metrics?.buckets.map((b) => ({
      ...b,
      label: formatAxisDate(b.t, metrics.bucket)
    })) ?? [];

  const trafficChartData =
    traffic?.byDay.map((b) => ({
      ...b,
      label: formatAxisDate(b.t, "day")
    })) ?? [];

  const uptimeChartData =
    uptime?.buckets.map((b) => ({
      ...b,
      label: formatAxisDate(b.t, uptime.bucket)
    })) ?? [];

  const resourceChartData =
    resources?.buckets.map((b) => ({
      ...b,
      label: formatAxisDate(b.t, resources.bucket),
      avgMemoryMb: b.avgMemoryBytes != null ? b.avgMemoryBytes / (1024 * 1024) : null
    })) ?? [];

  const latencyTrafficChartData =
    traffic?.latencyByDay.map((b) => ({
      ...b,
      label: formatAxisDate(b.t, "day")
    })) ?? [];

  const errorRateChartData =
    traffic?.errorRateByDay.map((b) => ({
      ...b,
      label: formatAxisDate(b.t, "day")
    })) ?? [];

  const runtimeHealthBadgeVariant = (
    status: UptimePayload["runtimeHealth"]["status"]
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "ok":
        return "default";
      case "recovering":
        return "secondary";
      case "image_missing":
      case "ensure_failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  const runtimeHealthLabel = (status: UptimePayload["runtimeHealth"]["status"]): string => {
    if (!status) return t("projectObservability.runtimeUnknown");
    return t(`projectObservability.runtimeHealth.${status}`);
  };

  const hasRuntimeData =
    (uptime?.probes.total ?? 0) > 0 || (resources?.buckets.length ?? 0) > 0 || uptime?.runtimeHealth.status;

  return (
    <div className="space-y-8">
      <ProjectRuntimeLogsPanel projectId={projectId} runtime={runtimeLogs} />

      {loadError ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loadError}
        </p>
      ) : null}

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-2">
          <Label htmlFor="obs-range">{t("projectObservability.range")}</Label>
          <Select
            value={String(rangeDays)}
            onValueChange={(v) => setRangeDays(v === "30" ? 30 : 7)}
          >
            <SelectTrigger id="obs-range" className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{t("projectObservability.last7Days")}</SelectItem>
              <SelectItem value="30">{t("projectObservability.last30Days")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="obs-bucket">{t("projectObservability.deployChartBucket")}</Label>
          <Select
            value={metricsBucket}
            onValueChange={(v) => setMetricsBucket(v === "hour" ? "hour" : "day")}
          >
            <SelectTrigger id="obs-bucket" className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">{t("projectObservability.bucketDaily")}</SelectItem>
              <SelectItem value="hour">{t("projectObservability.bucketHourly")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" onClick={() => void refreshAll()}>
          {t("common.refresh")}
        </Button>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-2">
            <Label htmlFor={`obs-dep-filter-${projectId}`}>{t("projectObservability.filterByDeployment")}</Label>
            <Select
              value={trafficDeploymentFilter?.trim() ? trafficDeploymentFilter.trim() : "__all__"}
              onValueChange={(v) => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    if (!v || v === "__all__") next.delete("deploymentId");
                    else next.set("deploymentId", v);
                    return next;
                  },
                  { replace: true }
                );
              }}
            >
              <SelectTrigger id={`obs-dep-filter-${projectId}`} className="w-[240px]">
                <SelectValue placeholder={t("projectObservability.allDeployments")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("projectObservability.allDeployments")}</SelectItem>
                {trafficDeploymentFilter?.trim() &&
                traffic &&
                !traffic.byDeployment.some((r) => r.deploymentId === trafficDeploymentFilter.trim()) ? (
                  <SelectItem value={trafficDeploymentFilter.trim()}>
                    {trafficDeploymentFilter.trim().slice(0, 8)}…
                  </SelectItem>
                ) : null}
                {(traffic?.byDeployment ?? []).map((row) => (
                  <SelectItem key={row.deploymentId} value={row.deploymentId}>
                    {row.shortId} ({row.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {trafficDeploymentFilter?.trim() ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete("deploymentId");
                    return next;
                  },
                  { replace: true }
                )
              }
            >
              {t("projectObservability.clearDeploymentFilter")}
            </Button>
          ) : null}
        </div>
      </div>

      <section className="space-y-6" aria-labelledby={`obs-runtime-heading-${projectId}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 id={`obs-runtime-heading-${projectId}`} className="text-xl font-semibold tracking-tight">
              {t("projectObservability.runtimeHeading")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("projectObservability.runtimeIntro")}</p>
          </div>
          {uptime?.runtimeHealth.status ? (
            <Badge variant={runtimeHealthBadgeVariant(uptime.runtimeHealth.status)}>
              {runtimeHealthLabel(uptime.runtimeHealth.status)}
            </Badge>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("projectObservability.uptimePercent")}</CardTitle>
              <CardDescription>{t("projectObservability.uptimeProbeWindow")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold tabular-nums">
                {uptime?.uptimePercent != null ? `${uptime.uptimePercent.toFixed(2)}%` : t("common.emDash")}
              </p>
              <p className="text-xs text-muted-foreground">
                {uptime
                  ? t("projectObservability.probeCounts", {
                      success: uptime.probes.success,
                      failed: uptime.probes.failed
                    })
                  : ""}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("projectObservability.probeLatency")}</CardTitle>
              <CardDescription>{t("projectObservability.p50p95")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold tabular-nums">
                {uptime?.latencyMs.p50 != null ? `${uptime.latencyMs.p50}ms` : t("common.emDash")} /{" "}
                {uptime?.latencyMs.p95 != null ? `${uptime.latencyMs.p95}ms` : t("common.emDash")}
              </p>
              {uptime?.latestProbe ? (
                <p className="text-xs text-muted-foreground">
                  {t("projectObservability.lastProbe")}{" "}
                  {new Date(uptime.latestProbe.probedAt).toLocaleString()}
                </p>
              ) : null}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("projectObservability.cpuUsage")}</CardTitle>
              <CardDescription>{t("projectObservability.containerCurrent")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold tabular-nums">
                {resources?.latest?.cpuPercent != null
                  ? `${resources.latest.cpuPercent.toFixed(1)}%`
                  : t("common.emDash")}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("projectObservability.memoryUsage")}</CardTitle>
              <CardDescription>{t("projectObservability.containerCurrent")}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-semibold tabular-nums">
                {resources?.latest
                  ? formatBytes(resources.latest.memoryBytes)
                  : t("common.emDash")}
              </p>
              {resources?.latest?.memoryLimitBytes ? (
                <p className="text-xs text-muted-foreground">
                  {t("projectObservability.ofLimit", {
                    limit: formatBytes(resources.latest.memoryLimitBytes)
                  })}
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>

        {!hasRuntimeData ? (
          <p className="rounded-lg border border-dashed border-border/80 px-4 py-6 text-sm text-muted-foreground">
            {t("projectObservability.noRuntimeSamples")}
          </p>
        ) : (
          <>
            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t("projectObservability.availabilityOverTime")}</CardTitle>
                  <CardDescription>{t("projectObservability.availabilityOverTimeDesc")}</CardDescription>
                </CardHeader>
                <CardContent className="pl-0">
                  {uptimeChartData.length > 0 ? (
                    <ChartContainer config={uptimeChartConfig} className="h-[260px] w-full">
                      <AreaChart data={uptimeChartData} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                        <YAxis width={40} tickLine={false} axisLine={false} domain={[0, 100]} unit="%" />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Area
                          type="monotone"
                          dataKey="availabilityPercent"
                          stroke="var(--color-availabilityPercent)"
                          fill="var(--color-availabilityPercent)"
                          fillOpacity={0.18}
                          strokeWidth={2}
                          dot={false}
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("projectObservability.noUptimeData")}</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("projectObservability.probeLatencyOverTime")}</CardTitle>
                  <CardDescription>{t("projectObservability.probeLatencyOverTimeDesc")}</CardDescription>
                </CardHeader>
                <CardContent className="pl-0">
                  {uptimeChartData.some((row) => row.latencyP50Ms != null || row.latencyP95Ms != null) ? (
                    <ChartContainer config={uptimeChartConfig} className="h-[260px] w-full">
                      <LineChart data={uptimeChartData} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                        <YAxis width={40} tickLine={false} axisLine={false} unit="ms" />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line
                          type="monotone"
                          dataKey="latencyP50Ms"
                          stroke="var(--color-latencyP50Ms)"
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="latencyP95Ms"
                          stroke="var(--color-latencyP95Ms)"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("projectObservability.noLatencyData")}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t("projectObservability.cpuOverTime")}</CardTitle>
                  <CardDescription>{t("projectObservability.cpuOverTimeDesc")}</CardDescription>
                </CardHeader>
                <CardContent className="pl-0">
                  {resourceChartData.some((row) => row.avgCpuPercent != null) ? (
                    <ChartContainer config={resourceChartConfig} className="h-[240px] w-full">
                      <LineChart data={resourceChartData} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                        <YAxis width={40} tickLine={false} axisLine={false} unit="%" />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line
                          type="monotone"
                          dataKey="avgCpuPercent"
                          stroke="var(--color-avgCpuPercent)"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ChartContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("projectObservability.noResourceData")}</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t("projectObservability.memoryOverTime")}</CardTitle>
                  <CardDescription>{t("projectObservability.memoryOverTimeDesc")}</CardDescription>
                </CardHeader>
                <CardContent className="pl-0">
                  {resourceChartData.some((row) => row.avgMemoryMb != null) ? (
                    <ChartContainer config={resourceChartConfig} className="h-[240px] w-full">
                      <AreaChart data={resourceChartData} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                        <YAxis width={48} tickLine={false} axisLine={false} unit=" MB" />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Area
                          type="monotone"
                          dataKey="avgMemoryMb"
                          stroke="var(--color-avgMemoryMb)"
                          fill="var(--color-avgMemoryMb)"
                          fillOpacity={0.18}
                          strokeWidth={2}
                          dot={false}
                        />
                      </AreaChart>
                    </ChartContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t("projectObservability.noResourceData")}</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {uptime?.runtimeHealth.detail ? (
          <p className="text-sm text-muted-foreground">{uptime.runtimeHealth.detail}</p>
        ) : null}
      </section>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("projectObservability.successRate")}</CardTitle>
            <CardDescription>{t("projectObservability.finishedBuildsInRange")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {metrics?.successRate != null ? `${(metrics.successRate * 100).toFixed(1)}%` : t("common.emDash")}
            </p>
            <p className="text-xs text-muted-foreground">
              {metrics
                ? t("projectObservability.okFailedCounts", {
                    ok: metrics.terminalInRange.success,
                    failed: metrics.terminalInRange.failed
                  })
                : ""}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("projectObservability.buildDuration")}</CardTitle>
            <CardDescription>{t("projectObservability.p50p95")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {formatDuration(metrics?.buildDurationSeconds.p50 ?? null, t("common.emDash"))} /{" "}
              {formatDuration(metrics?.buildDurationSeconds.p95 ?? null, t("common.emDash"))}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("projectObservability.backlog")}</CardTitle>
            <CardDescription>{t("projectObservability.queuedBuilding")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {metrics ? `${metrics.backlog.queued} / ${metrics.backlog.building}` : t("common.emDash")}
            </p>
            {metrics?.backlog.oldestQueuedAt ? (
              <p className="text-xs text-muted-foreground">
                {t("projectObservability.oldestQueued")}{" "}
                {new Date(metrics.backlog.oldestQueuedAt).toLocaleString()}
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("projectObservability.previewRequests")}</CardTitle>
            <CardDescription>{t("projectObservability.requestsInRange")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold">
              {traffic != null
                ? traffic.byDay.reduce((sum, row) => sum + row.count, 0).toLocaleString()
                : t("common.emDash")}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("projectObservability.deploymentsOverTime")}</CardTitle>
          <CardDescription>
            {t("projectObservability.countsByBucket", {
              bucket: metrics?.bucket ?? t("projectObservability.bucket")
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="pl-0">
          {bucketChartData.length > 0 ? (
            <ChartContainer config={deployChartConfig} className="h-[280px] w-full">
              <LineChart data={bucketChartData} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis width={32} tickLine={false} axisLine={false} allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey="success"
                  stroke="var(--color-success)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="var(--color-failed)"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartContainer>
          ) : (
            <p className="text-sm text-muted-foreground">{t("projectObservability.noDeployData")}</p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("projectObservability.previewTraffic")}</CardTitle>
            <CardDescription>{t("projectObservability.requestsPerDay")}</CardDescription>
          </CardHeader>
          <CardContent className="pl-0">
            {trafficChartData.length > 0 ? (
              <ChartContainer config={trafficChartConfig} className="h-[240px] w-full">
                <BarChart data={trafficChartData} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis width={32} tickLine={false} axisLine={false} allowDecimals={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={4} />
                </BarChart>
              </ChartContainer>
            ) : (
              <p className="text-sm text-muted-foreground">{t("projectObservability.noPreviewTraffic")}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("projectObservability.topClientIps")}</CardTitle>
            <CardDescription>{t("projectObservability.fromPreviewTraffic")}</CardDescription>
          </CardHeader>
          <CardContent>
            {traffic && traffic.topIps.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("projectObservability.ipCol")}</TableHead>
                    <TableHead className="text-right">{t("projectObservability.requestsCol")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {traffic.topIps.map((row) => (
                    <TableRow key={row.clientIp}>
                      <TableCell className="font-mono text-xs">{row.clientIp}</TableCell>
                      <TableCell className="text-right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">{t("projectObservability.noIpData")}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("projectObservability.requestLatency")}</CardTitle>
            <CardDescription>{t("projectObservability.requestLatencyDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="pl-0">
            {latencyTrafficChartData.some((row) => row.p50Ms != null || row.p95Ms != null) ? (
              <ChartContainer config={latencyTrafficChartConfig} className="h-[240px] w-full">
                <LineChart data={latencyTrafficChartData} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis width={40} tickLine={false} axisLine={false} unit="ms" />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Line type="monotone" dataKey="p50Ms" stroke="var(--color-p50Ms)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="p95Ms" stroke="var(--color-p95Ms)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartContainer>
            ) : (
              <p className="text-sm text-muted-foreground">{t("projectObservability.noLatencyData")}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("projectObservability.requestErrorRate")}</CardTitle>
            <CardDescription>{t("projectObservability.requestErrorRateDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="pl-0">
            {errorRateChartData.some((row) => row.errorRate != null) ? (
              <ChartContainer config={errorRateChartConfig} className="h-[240px] w-full">
                <AreaChart data={errorRateChartData} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} />
                  <YAxis width={40} tickLine={false} axisLine={false} domain={[0, 100]} unit="%" />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="errorRate"
                    stroke="var(--color-errorRate)"
                    fill="var(--color-errorRate)"
                    fillOpacity={0.16}
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ChartContainer>
            ) : (
              <p className="text-sm text-muted-foreground">{t("projectObservability.noErrorRateData")}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {traffic && traffic.byDeployment.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("projectObservability.deploymentsTrafficTitle")}</CardTitle>
            <CardDescription>{t("projectObservability.deploymentsTrafficDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("deployment.deploymentLabel")}</TableHead>
                  <TableHead className="text-right">{t("projectObservability.requestsCol")}</TableHead>
                  <TableHead className="w-[1%] text-right">{t("projectObservability.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {traffic.byDeployment.map((row) => (
                  <TableRow key={row.deploymentId}>
                    <TableCell className="font-mono text-xs">{row.shortId}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSearchParams(
                              (prev) => {
                                const next = new URLSearchParams(prev);
                                next.set("deploymentId", row.deploymentId);
                                return next;
                              },
                              { replace: true }
                            );
                          }}
                        >
                          {t("projectObservability.filter")}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" asChild>
                          <Link
                            to={`/deployments/${row.deploymentId}`}
                            aria-label={t("projectObservability.openDeploymentAria", {
                              shortId: row.shortId
                            })}
                          >
                            {t("projectObservability.openDeployment")}
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {traffic && traffic.byStatus.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("projectObservability.responseStatusPreview")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("projectObservability.statusCol")}</TableHead>
                  <TableHead className="text-right">{t("projectObservability.requestsCol")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {traffic.byStatus.map((row) => (
                  <TableRow key={row.statusCode}>
                    <TableCell>{row.statusCode}</TableCell>
                    <TableCell className="text-right">{row.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      {projectRole !== "viewer" ? <Card>
        <CardHeader>
          <CardTitle>{t("projectObservability.webhookAlerts")}</CardTitle>
          <CardDescription>{t("projectObservability.webhookAlertsDesc", { projectName })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-3">
            <Label htmlFor="test-webhook">{t("projectObservability.testWebhookUrl")}</Label>
            <div className="flex flex-wrap gap-2">
              <Input
                id="test-webhook"
                className="max-w-md"
                placeholder="https://example.com/hooks/deployher"
                value={testWebhookUrl}
                onChange={(e) => setTestWebhookUrl(e.target.value)}
                autoComplete="off"
              />
              <Button type="button" variant="secondary" onClick={() => void handleTestWebhook()}>
                {t("projectObservability.sendTest")}
              </Button>
            </div>
            {testResult ? <p className="text-sm text-muted-foreground">{testResult}</p> : null}
          </div>

          <div className="space-y-3">
            <Label htmlFor="new-dest">{t("projectObservability.addWebhookDestination")}</Label>
            <div className="flex flex-wrap gap-2">
              <Input
                id="new-dest"
                className="max-w-md"
                placeholder="https://…"
                value={newWebhookUrl}
                onChange={(e) => setNewWebhookUrl(e.target.value)}
                autoComplete="off"
              />
              <Button type="button" onClick={() => void handleAddDestination()}>
                {t("projectObservability.add")}
              </Button>
            </div>
            {destinations.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {destinations.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2"
                  >
                    <span className="min-w-0 truncate font-mono text-xs">{d.webhookUrl}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleDeleteDestination(d.id)}
                    >
                      {t("projectObservability.remove")}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t("projectObservability.noDestinationsYet")}</p>
            )}
          </div>

          <div className="space-y-3 border-t border-border/60 pt-6">
            <h3 className="text-sm font-medium">{t("projectObservability.rulesHeading")}</h3>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 md:items-end">
              <div className="space-y-2">
                <Label>{t("projectObservability.destination")}</Label>
                <Select value={ruleDestinationId} onValueChange={setRuleDestinationId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("projectObservability.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {destinations.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        {t("projectObservability.addDestinationFirst")}
                      </SelectItem>
                    ) : (
                      destinations.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.webhookUrl.slice(0, 48)}
                          {d.webhookUrl.length > 48 ? "…" : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("projectObservability.type")}</Label>
                <Select
                  value={ruleType}
                  onValueChange={(v) =>
                    setRuleType(v === "queue_stall" ? "queue_stall" : "consecutive_failures")
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consecutive_failures">
                      {t("projectObservability.ruleConsecutiveFailures")}
                    </SelectItem>
                    <SelectItem value="queue_stall">{t("projectObservability.ruleQueueStall")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rule-threshold">
                  {ruleType === "consecutive_failures"
                    ? t("projectObservability.failureCount")
                    : t("projectObservability.maxQueueAge")}
                </Label>
                <Input
                  id="rule-threshold"
                  inputMode="numeric"
                  value={ruleThreshold}
                  onChange={(e) => setRuleThreshold(e.target.value)}
                />
              </div>
              <Button type="button" onClick={() => void handleAddRule()}>
                {t("projectObservability.addRule")}
              </Button>
            </div>

            {rules.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("projectObservability.type")}</TableHead>
                    <TableHead>{t("projectObservability.threshold")}</TableHead>
                    <TableHead>{t("projectObservability.webhookCol")}</TableHead>
                    <TableHead>{t("projectObservability.enabled")}</TableHead>
                    <TableHead className="text-right">{t("projectObservability.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        {r.ruleType === "consecutive_failures"
                          ? t("projectObservability.ruleConsecutiveFailures")
                          : t("projectObservability.ruleQueueStall")}
                      </TableCell>
                      <TableCell>{r.threshold}</TableCell>
                      <TableCell className="max-w-[200px] truncate font-mono text-xs">
                        {r.destinationWebhookUrl}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleToggleRule(r)}
                        >
                          {r.enabled ? t("projectObservability.on") : t("projectObservability.off")}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void handleDeleteRule(r.id)}
                        >
                          {t("projectObservability.delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">{t("projectObservability.noRulesYet")}</p>
            )}
          </div>
        </CardContent>
      </Card> : null}
    </div>
  );
};
